import { useCurrentEditor } from '@tiptap/react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';

import { Icons } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';

import { Button } from '../../Button';
import { IconButton } from '../../IconButton';
import { Input } from '../../Input';
import { XStack, YStack } from '../../layout';
import {
  useEditorContainer,
  useEditorFloatingContainer,
} from '../base-editor-context';
import { editorEventBus } from '../utils/event';
import { isFindShortcut } from '../utils/keyboard';

import type { SearchAndReplaceStorage } from '../extensions/search-and-replace';
import type { Editor } from '@tiptap/core';

function isSearchAndReplaceStorage(
  value: unknown
): value is SearchAndReplaceStorage {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.searchTerm === 'string' &&
    typeof record.replaceTerm === 'string' &&
    typeof record.caseSensitive === 'boolean' &&
    typeof record.resultIndex === 'number' &&
    Array.isArray(record.results)
  );
}

function getStorage(editor: Editor | null): SearchAndReplaceStorage | null {
  const storage = editor?.storage as Record<string, unknown> | undefined;
  const candidate = storage?.searchAndReplace;
  return isSearchAndReplaceStorage(candidate) ? candidate : null;
}

/** Gap in rem above the editor when the find bar floats above it. */
const EDITOR_FLOAT_GAP_REM = 0.5;

export function EditorSearchBar({
  portalContainer,
}: {
  portalContainer?: HTMLElement | null;
} = {}) {
  const { editor } = useCurrentEditor();
  const editorContainer = useEditorContainer();
  const floatingContainer = useEditorFloatingContainer();

  const [isOpen, setIsOpen] = useState(false);
  const [showReplace, setShowReplace] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [replaceTerm, setReplaceTerm] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [resultCount, setResultCount] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const scrollOnNextUpdate = useRef(false);
  const [position, setPosition] = useState({ top: 0, right: 0 });

  // no scroll container (findScrollableParent=false), e.g. a chat copyable block
  const anchorToEditor = editorContainer === document.body;
  const hasPortalOverride = portalContainer !== undefined;

  // anchor to the scroll container, or the editor itself when it has none
  useEffect(() => {
    if (!isOpen || !editor || hasPortalOverride) return;

    const anchor = anchorToEditor
      ? ((editor.view.dom.closest('.lb-editor') as HTMLElement | null) ??
        (editor.view.dom as HTMLElement))
      : editorContainer;
    if (!anchor) return;

    const inset = anchorToEditor ? 12 : 20;

    const update = () => {
      const rect = anchor.getBoundingClientRect();
      setPosition({
        top: anchorToEditor ? rect.top : rect.top + inset,
        right: window.innerWidth - rect.right + inset,
      });
    };

    update();
    window.addEventListener('scroll', update, { passive: true, capture: true });
    window.addEventListener('resize', update, { passive: true });
    return () => {
      window.removeEventListener('scroll', update, { capture: true });
      window.removeEventListener('resize', update);
    };
  }, [isOpen, editor, editorContainer, anchorToEditor, hasPortalOverride]);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => searchInputRef.current?.focus());
  }, [isOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearchTerm('');
    setReplaceTerm('');
    setShowReplace(false);
    setCaseSensitive(false);
    editor?.commands.setSearchTerm('');
    editor?.commands.setReplaceTerm('');
    editor?.commands.setCaseSensitive(false);
    editor?.commands.resetIndex();
    editor?.commands.focus();
  }, [editor]);

  // Listen for Mod-f from the extension via event bus
  useEffect(() => {
    const sub = editorEventBus.on('EDITOR_OPEN_SEARCH_BAR', (event) => {
      if (!editor || event.data.editor !== editor) return;
      open();
    });
    return () => sub?.unsubscribe();
  }, [open, editor]);

  // Sync search results from extension storage on transactions
  useEffect(() => {
    if (!editor || !isOpen) return;

    const updateResults = () => {
      const s = getStorage(editor);
      if (!s) {
        setResultCount(0);
        setCurrentIndex(0);
        return;
      }
      setResultCount(s.results.length);
      setCurrentIndex(s.resultIndex);

      if (scrollOnNextUpdate.current && s.results.length > 0) {
        scrollOnNextUpdate.current = false;
        const result = s.results[s.resultIndex];
        if (result) {
          requestAnimationFrame(() => {
            editor.commands.setTextSelection(result);
            const { node } = editor.view.domAtPos(result.from);
            const el = node instanceof HTMLElement ? node : node.parentElement;
            el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          });
        }
      }
    };

    updateResults();
    editor.on('transaction', updateResults);
    return () => {
      editor.off('transaction', updateResults);
    };
  }, [editor, isOpen]);

  // Push search term to extension
  useEffect(() => {
    if (!editor || !isOpen) return;
    editor.commands.setSearchTerm(searchTerm);
  }, [editor, isOpen, searchTerm]);

  // Push replace term to extension
  useEffect(() => {
    if (!editor || !isOpen) return;
    editor.commands.setReplaceTerm(replaceTerm);
  }, [editor, isOpen, replaceTerm]);

  // Push case sensitivity to extension
  useEffect(() => {
    if (!editor || !isOpen) return;
    editor.commands.setCaseSensitive(caseSensitive);
  }, [editor, isOpen, caseSensitive]);

  const scrollToCurrentResult = useCallback(() => {
    if (!editor) return;
    const s = getStorage(editor);
    if (!s) return;
    const result = s.results[s.resultIndex];
    if (result) {
      editor.commands.setTextSelection(result);
      const { node } = editor.view.domAtPos(result.from);
      const element = node instanceof HTMLElement ? node : node.parentElement;
      element?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [editor]);

  const goToNext = useCallback(() => {
    editor?.commands.nextSearchResult();
    scrollToCurrentResult();
  }, [editor, scrollToCurrentResult]);

  const goToPrevious = useCallback(() => {
    editor?.commands.previousSearchResult();
    scrollToCurrentResult();
  }, [editor, scrollToCurrentResult]);

  const handleReplace = useCallback(() => {
    editor?.commands.replace();
    scrollOnNextUpdate.current = true;
  }, [editor]);

  const handleReplaceAll = useCallback(() => {
    editor?.commands.replaceAll();
    scrollOnNextUpdate.current = true;
  }, [editor]);

  const handleSearchKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (isFindShortcut(e.nativeEvent)) {
        // Already in the editor's find bar — keep Cmd/Ctrl+F here instead of
        // letting it bubble to ancestor find handlers (e.g. useVirtualListFind).
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
        e.currentTarget.select();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close();
      } else if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        goToNext();
      }
    },
    [close, goToNext, goToPrevious]
  );

  const handleReplaceKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (isFindShortcut(e.nativeEvent)) {
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
        searchInputRef.current?.select();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleReplace();
      }
    },
    [close, handleReplace]
  );

  const resolvedPortalContainer = hasPortalOverride
    ? portalContainer
    : floatingContainer;
  if (!isOpen || !editor || !resolvedPortalContainer) return null;

  return createPortal(
    <YStack
      data-editor-floating-ui=""
      className={cn(
        !hasPortalOverride && 'fixed',
        'z-20 pointer-events-auto',
        'rounded-lg smoothing-default',
        'border border-border-strong',
        'bg-surface',
        'shadow-xl',
        'p-2',
        'gap-2',
        'min-w-[320px]'
      )}
      style={
        hasPortalOverride
          ? undefined
          : {
              top: position.top,
              right: position.right,
              // float above the editor's top edge so it never covers content
              transform: anchorToEditor
                ? `translateY(calc(-100% - ${EDITOR_FLOAT_GAP_REM}rem))`
                : undefined,
            }
      }
    >
      {/* Find row */}
      <XStack className="items-center gap-1.5">
        <div className="relative flex-1">
          <Input
            ref={searchInputRef}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Find"
            variant="onSurface"
            inputSize="sm"
            className="pr-12"
            autoFocus
          />
          {searchTerm && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-foreground-muted tabular-nums whitespace-nowrap pointer-events-none">
              {resultCount > 0 ? currentIndex + 1 : 0}/{resultCount}
            </span>
          )}
        </div>
        <IconButton
          icon={Icons.chevronUp}
          size="sm"
          variant="ghost"
          onClick={goToPrevious}
          tooltip="Previous"
          tooltipOptions={{ hotkey: ['shift', 'enter'] }}
          disabled={resultCount === 0}
        />
        <IconButton
          icon={Icons.chevronDown}
          size="sm"
          variant="ghost"
          onClick={goToNext}
          tooltip="Next"
          tooltipOptions={{ hotkey: ['enter'] }}
          disabled={resultCount === 0}
        />
        <IconButton
          icon={Icons.caseSensitive}
          size="sm"
          variant="ghost"
          onClick={() => setCaseSensitive(!caseSensitive)}
          className={cn(caseSensitive && 'bg-secondary-hover')}
          tooltip="Match case"
          scale={1.15}
        />
        {editor.isEditable && (
          <IconButton
            icon={Icons.replaceAll}
            size="sm"
            variant="ghost"
            onClick={() => setShowReplace(!showReplace)}
            className={cn(showReplace && 'bg-secondary-hover')}
            tooltip={showReplace ? 'Hide replace' : 'Show replace'}
          />
        )}
        <IconButton
          icon={Icons.cancel}
          size="sm"
          variant="ghost"
          onClick={close}
          tooltip="Close"
          tooltipOptions={{ hotkey: ['esc'] }}
        />
      </XStack>

      {/* Replace row */}
      {showReplace && editor.isEditable && (
        <XStack className="items-center gap-1.5">
          <Input
            value={replaceTerm}
            onChange={(e) => setReplaceTerm(e.target.value)}
            onKeyDown={handleReplaceKeyDown}
            placeholder="Replace"
            variant="onSurface"
            inputSize="sm"
            className="flex-1"
          />
          <Button
            onClick={handleReplace}
            disabled={resultCount === 0}
            variant="outline"
            size="sm"
            textSize="xs"
          >
            Replace
          </Button>
          <Button
            onClick={handleReplaceAll}
            disabled={resultCount === 0}
            variant="outline"
            size="sm"
            textSize="xs"
          >
            All
          </Button>
        </XStack>
      )}
    </YStack>,
    resolvedPortalContainer
  );
}
